import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

class BuyMoreSaveMore extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#setupActiveCard();
    this.#setupEventListeners();
    this.#syncQuantity();
  }

  #setupActiveCard() {
    let activeCard = this.querySelector('.bmsm-card.active');
    if (!activeCard) {
      activeCard = this.querySelector('.bmsm-card');
      if (activeCard) {
        activeCard.classList.add('active');
        activeCard.setAttribute('aria-checked', 'true');
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (closestSection) {
      closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    }
  }

  #setupEventListeners() {
    const cards = this.querySelectorAll('.bmsm-card');
    cards.forEach(card => {
      // Setup click selection
      card.addEventListener('click', () => {
        cards.forEach(c => {
          c.classList.remove('active');
          c.setAttribute('aria-checked', 'false');
        });
        card.classList.add('active');
        card.setAttribute('aria-checked', 'true');
        this.#syncQuantity();
      });

      // Handle keyboard navigation (Space/Enter)
      card.addEventListener('keydown', (event) => {
        if (event.key === ' ' || event.key === 'Enter') {
          event.preventDefault();
          card.click();
        }
      });
    });

    // Listen to variant changes
    const closestSection = this.closest('.shopify-section, dialog');
    if (closestSection) {
      closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    }
  }

  #syncQuantity() {
    const activeCard = this.querySelector('.bmsm-card.active');
    if (!activeCard) return;
    const quantity = parseInt(activeCard.dataset.quantity) || 1;
    const discountCode = activeCard.dataset.discountCode || '';

    // Find the product form
    const productComponent = this.closest('product-component');
    if (productComponent) {
      const form = productComponent.querySelector('form[data-type="add-to-cart-form"]');
      if (form) {
        // Find the main variant ID input in the form
        const mainIdInput = form.querySelector('input[name="id"]:not([name*="items"])');
        if (!mainIdInput) return;

        mainIdInput.disabled = false;

        const qtyInputs = form.querySelectorAll('input[name="quantity"]:not([name*="items"])');
        if (qtyInputs.length > 0) {
          qtyInputs.forEach(input => {
            input.disabled = false;
            input.value = quantity;
            input.dispatchEvent(new Event('change', { bubbles: true }));
            input.dispatchEvent(new Event('input', { bubbles: true }));

            // Hide standard quantity inputs so they don't look duplicate
            const wrapper = input.closest('.quantity-selector-wrapper');
            if (wrapper) {
              wrapper.style.display = 'none';
            }
          });
        } else {
          // If no quantity input is in the form, create/update a hidden one
          let hiddenInput = form.querySelector('input[name="quantity"][type="hidden"]');
          if (!hiddenInput) {
            hiddenInput = document.createElement('input');
            hiddenInput.type = 'hidden';
            hiddenInput.name = 'quantity';
            hiddenInput.className = 'bmsm-injected-input';
            form.appendChild(hiddenInput);
          }
          hiddenInput.value = quantity;
          hiddenInput.disabled = false;
          hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        if (discountCode) {
          // Apply/update discount code via Shopify cart AJAX
          const updateUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_update_url) || '/cart/update.js';
          fetch(updateUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              discount: discountCode
            })
          }).catch(err => console.warn('[buy-more-save-more] Failed to update discount code:', err));
        }
      }
    }
  }

  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const newBlock = detail.html.querySelector(`buy-more-save-more[data-block-id="${this.dataset.blockId}"]`);
        if (newBlock) {
          this.innerHTML = newBlock.innerHTML;
          // Re-bind click event listeners and sync quantity
          this.#setupEventListeners();
          this.#syncQuantity();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[buy-more-save-more] Variant pricing update failed:', error);
      });
  };
}

if (!customElements.get('buy-more-save-more')) {
  customElements.define('buy-more-save-more', BuyMoreSaveMore);
}
